import { useMemo, useCallback } from 'react';
import { Requisition, RequisitionItem } from '../../types';
import { useRequisitions, useCreateRequisition, useUpdateRequisition, useDeleteRequisition } from '../../hooks/useRequisitions';
import { usePaymentOrders, useCreatePaymentOrder, useUpdatePaymentOrder } from '../../hooks/usePaymentOrders';
import { useDeliveryNotes, useCreateDeliveryNote, useUpdateDeliveryNote } from '../../hooks/useDeliveryNotes';
import { useProviders } from '../../hooks/useProviders';
import { useToast } from '../../contexts/ToastContext';
import {
  ItemRow, itemsOf, flattenItems, buildPaymentOrderPayload, buildDeliveryNotePayload, withUpdatedItems,
  mergePaymentOrderItems, findOrderForReq, mergeDeliveryNoteItems, findNoteForReq,
} from './requisitionWorkflowHelpers';

export interface RequisitionListFilters {
  vessel?: string; // 'all' or vessel/HQ name
  requisitionId?: string; // 'all' or requisition documentId
}

export function useRequisitionWorkflow(listFilters?: RequisitionListFilters) {
  const { showToast } = useToast();
  const vessel = listFilters?.vessel && listFilters.vessel !== 'all' ? listFilters.vessel : undefined;
  const reqId = listFilters?.requisitionId && listFilters.requisitionId !== 'all' ? listFilters.requisitionId : undefined;

  // Filters are applied server-side via Strapi bracket-notation query params
  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 200 },
    ...(vessel || reqId ? {
      filters: {
        ...(vessel ? { vessel: { $eq: vessel } } : {}),
        ...(reqId ? { documentId: { $eq: reqId } } : {}),
      },
    } : {}),
  }), [vessel, reqId]);
  const providersQO = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 200 },
  }), []);

  const { data: fetched = [], isLoading, error, refetch } = useRequisitions(queryOptions);
  const { data: providers = [] } = useProviders(providersQO);
  const docsQO = useMemo(() => ({ pagination: { pageSize: 1000 } }), []);
  const { data: paymentOrders = [], refetch: refetchPOs } = usePaymentOrders(docsQO);
  const { data: deliveryNotes = [], refetch: refetchDNs } = useDeliveryNotes(docsQO);

  // Safety net in case the backend ignores a filter param
  const requisitions = useMemo(() => (fetched || []).filter((r: any) =>
    (!vessel || r.vessel === vessel) &&
    (!reqId || (r.documentId || String(r.id)) === reqId)
  ), [fetched, vessel, reqId]);

  const updateReq = useUpdateRequisition();
  const createReq = useCreateRequisition(() => refetch());
  const deleteReq = useDeleteRequisition();
  const createPO = useCreatePaymentOrder();
  const updatePO = useUpdatePaymentOrder();
  const createDN = useCreateDeliveryNote();
  const updateDN = useUpdateDeliveryNote();

  const rows: ItemRow[] = useMemo(() => flattenItems(requisitions), [requisitions]);

  const saveItems = useCallback(
    (req: Requisition, items: RequisitionItem[]) =>
      updateReq.execute({ id: parseInt(req.id), data: withUpdatedItems(req, items) }),
    [updateReq]
  );

  /** One payment order per requisition: merge into the existing order if there
   *  is one, otherwise create it with ALL the requisition's items.
   *  approvedIds: requisition item ids to flag as approved on the order. */
  const ensurePaymentOrder = useCallback(async (
    req: Requisition, approvedIds: Set<string>, employeeName?: string
  ): Promise<string | undefined> => {
    const existing = findOrderForReq(paymentOrders, req);
    if (existing) {
      const merged = mergePaymentOrderItems(existing, req, approvedIds);
      await updatePO.execute({ id: parseInt(existing.id), data: { ...existing, documentId: existing.documentId, ...merged } });
      showToast(`Payment order ${existing.orderNumber} updated`, 'success');
      refetchPOs();
      return existing.documentId || String(existing.id);
    }
    const po: any = await createPO.execute(
      buildPaymentOrderPayload(req, providers as any[], employeeName, approvedIds));
    showToast(`Payment order created for requisition ${req.formNumber}`, 'success');
    refetchPOs();
    return po?.documentId || po?.id?.toString();
  }, [paymentOrders, providers, createPO, updatePO, refetchPOs, showToast]);

  /** One delivery note per requisition: merge new items into the existing note if any */
  const ensureDeliveryNote = useCallback(async (
    req: Requisition, items: RequisitionItem[]
  ): Promise<string | undefined> => {
    const existing = findNoteForReq(deliveryNotes, req);
    if (existing) {
      const technicalPart = mergeDeliveryNoteItems(existing, req, items);
      await updateDN.execute({ id: parseInt(existing.id), data: { ...existing, documentId: existing.documentId, technicalPart } });
      showToast(`Item(s) added to delivery note ${existing.deliveryNoteNumber}`, 'success');
      refetchDNs();
      return existing.documentId || String(existing.id);
    }
    const dn: any = await createDN.execute(buildDeliveryNotePayload(req, items));
    showToast(`Delivery note created for requisition ${req.formNumber}`, 'success');
    refetchDNs();
    return dn?.documentId || dn?.id?.toString();
  }, [deliveryNotes, createDN, updateDN, refetchDNs, showToast]);

  /** Change one item's status; approving adds it to the requisition's payment
   *  order, delivery-noted to its delivery note (created on first use).
   *  employeeName: the employee chosen to handle the payment order (approval popup). */
  const updateItemStatus = useCallback(async (
    req: Requisition, item: RequisitionItem, newStatus: RequisitionItem['status'], employeeName?: string
  ) => {
    try {
      const extras: Partial<RequisitionItem> = {};
      let paymentOrderId: string | undefined;
      let deliveryNoteId: string | undefined;

      if (newStatus === 'approved') {
        paymentOrderId = await ensurePaymentOrder(req, new Set([item.id]), employeeName);
      } else if (newStatus === 'delivery-noted') {
        deliveryNoteId = await ensureDeliveryNote(req, [item]);
      } else if (newStatus === 'purchased') {
        extras.purchaseDate = new Date().toISOString().split('T')[0];
      } else if (newStatus === 'delivered') {
        extras.deliveryDate = new Date().toISOString().split('T')[0];
      }

      const items = itemsOf(req).map((i) => i.id === item.id
        ? { ...i, status: newStatus, ...extras,
            ...(paymentOrderId ? { paymentOrderId } : {}),
            ...(deliveryNoteId ? { deliveryNoteId } : {}) }
        : i);
      await saveItems(req, items);
      refetch();
    } catch (e: any) {
      showToast(e?.message || 'Failed to update item status', 'error');
    }
  }, [ensurePaymentOrder, ensureDeliveryNote, saveItems, refetch, showToast]);

  /** Create/extend one payment order per requisition for the given rows.
   *  The covered items are approved (unless already further along) and linked
   *  to the order. */
  const createPaymentOrderForRows = useCallback(async (selected: ItemRow[]) => {
    try {
      const byReq = new Map<string, ItemRow[]>();
      selected.forEach((row) => {
        const key = row.requisition.documentId || String(row.requisition.id);
        if (!byReq.has(key)) byReq.set(key, []);
        byReq.get(key)!.push(row);
      });
      const groups = Array.from(byReq.values());
      for (const group of groups) {
        const req = group[0].requisition;
        const ids = new Set(group.map((g) => g.item.id));
        const poId = await ensurePaymentOrder(req, ids);
        const updatedItems = itemsOf(req).map((i) => ids.has(i.id)
          ? { ...i,
              status: (i.status === 'pending' || i.status === 'rejected' ? 'approved' : i.status) as RequisitionItem['status'],
              ...(poId ? { paymentOrderId: poId } : {}) }
          : i);
        await saveItems(req, updatedItems);
      }
      refetch();
    } catch (e: any) {
      showToast(e?.message || 'Failed to create payment order', 'error');
    }
  }, [ensurePaymentOrder, saveItems, refetch, showToast]);

  /** Create/extend one delivery note per requisition for the selected rows,
   *  marking items delivery-noted */
  const createDeliveryNoteForRows = useCallback(async (selected: ItemRow[]) => {
    try {
      const byReq = new Map<string, ItemRow[]>();
      selected.forEach((row) => {
        const key = row.requisition.documentId || String(row.requisition.id);
        if (!byReq.has(key)) byReq.set(key, []);
        byReq.get(key)!.push(row);
      });
      const groups = Array.from(byReq.values());
      for (const group of groups) {
        const req = group[0].requisition;
        const items = group.map((g) => g.item);
        const noteId = await ensureDeliveryNote(req, items);
        const ids = new Set(items.map((i) => i.id));
        const updatedItems = itemsOf(req).map((i) =>
          ids.has(i.id) ? { ...i, status: 'delivery-noted' as const, ...(noteId ? { deliveryNoteId: noteId } : {}) } : i);
        await saveItems(req, updatedItems);
      }
      showToast(`Delivery note updated for ${selected.length} item(s)`, 'success');
      refetch();
    } catch (e: any) {
      showToast(e?.message || 'Failed to create delivery note', 'error');
    }
  }, [ensureDeliveryNote, saveItems, refetch, showToast]);

  /** Add a new item to a requisition, or update an existing one */
  const saveItem = useCallback(async (
    req: Requisition, itemData: Partial<RequisitionItem>, editingItemId?: string | null
  ) => {
    try {
      let items: RequisitionItem[];
      if (editingItemId) {
        items = itemsOf(req).map((i) => (i.id === editingItemId ? { ...i, ...itemData } : i));
      } else {
        const newItem: RequisitionItem = {
          id: Date.now().toString(), itemNumber: (req.items?.length || 0) + 1,
          item: '', qty: 0, rob: '', appQty: 0, unitPrice: 0, supplier: '', remarks: '',
          ...itemData, status: 'pending',
        } as RequisitionItem;
        items = [...itemsOf(req), newItem];
      }
      await saveItems(req, items);
      refetch();
      return true;
    } catch (e: any) {
      showToast(e?.message || 'Failed to save item', 'error');
      return false;
    }
  }, [saveItems, refetch, showToast]);

  const deleteItem = useCallback(async (req: Requisition, itemId: string) => {
    try {
      await saveItems(req, itemsOf(req).filter((i) => i.id !== itemId));
      refetch();
    } catch (e: any) {
      showToast(e?.message || 'Failed to delete item', 'error');
    }
  }, [saveItems, refetch, showToast]);

  const createRequisition = useCallback(async (data: Partial<Requisition>) => {
    try { await createReq.execute(data); return true; }
    catch (e: any) { showToast(e?.message || 'Failed to create requisition', 'error'); return false; }
  }, [createReq, showToast]);

  /** Update the requisition header fields (Edit Req form) */
  const updateRequisition = useCallback(async (req: Requisition, data: Partial<Requisition>) => {
    try {
      await updateReq.execute({ id: parseInt(req.id), data: { ...req, ...data, documentId: req.documentId } });
      showToast(`Requisition ${data.formNumber || req.formNumber} updated`, 'success');
      refetch();
      return true;
    } catch (e: any) {
      showToast(e?.message || 'Failed to update requisition', 'error');
      return false;
    }
  }, [updateReq, refetch, showToast]);

  /** Delete a whole requisition (and its inline items with it) */
  const deleteRequisition = useCallback(async (req: Requisition) => {
    try {
      await deleteReq.execute(req);
      showToast(`Requisition ${req.formNumber} deleted`, 'success');
      refetch();
    } catch (e: any) {
      showToast(e?.message || 'Failed to delete requisition', 'error');
    }
  }, [deleteReq, refetch, showToast]);

  return {
    requisitions, rows, isLoading, error, refetch, providers,
    updateItemStatus, createDeliveryNoteForRows, createPaymentOrderForRows, saveItem, deleteItem,
    createRequisition, updateRequisition, deleteRequisition,
  };
}
